import React, { ElementType, ComponentPropsWithRef, useState, useEffect, useCallback } from 'react';

/**
 * ClickableElement is a higher-order component that adds click animation 
 * to any element that can be clicked.
 */
type ClickableElementProps<T extends ElementType> = {
  as?: T;
  activeClassName?: string;
  clickableClassName?: string;
} & ComponentPropsWithRef<T>;

const ClickableElement = <T extends ElementType = 'div'>({
  as,
  children,
  className = '',
  activeClassName = 'active:scale-98',
  clickableClassName = 'transition-all duration-150 cursor-pointer',
  onClick,
  ...props
}: ClickableElementProps<T>) => {
  const Component = as || 'div';
  const [isActive, setIsActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.matchMedia('(hover: none), (pointer: coarse)').matches);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    setIsActive(true);
    props.onTouchStart?.(e);
  }, [isMobile, props]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    // Get the touch and element
    const touch = e.touches[0];
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();

    // Check if touch is still within element bounds
    const isWithinBounds = 
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom;

    setIsActive(isWithinBounds);
    props.onTouchMove?.(e);
  }, [isMobile, props]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    setIsActive(false);
    props.onTouchEnd?.(e);
  }, [isMobile, props]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    onClick?.(e);
  }, [onClick]);

  // Only apply touch handlers on mobile
  const mobileProps = isMobile ? {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
    style: {
      ...props.style,
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      transform: isActive ? 'scale(0.98)' : undefined,
      transition: 'transform 150ms'
    }
  } : {};

  const combinedClassName = `${clickableClassName} ${isMobile ? '' : activeClassName} ${className}`;

  return (
    <Component 
      className={combinedClassName}
      onClick={handleClick}
      {...props}
      {...mobileProps}
    >
      {children}
    </Component>
  );
};

export default ClickableElement; 